import { useCallback, useEffect, useRef, useState } from 'react';

// PECA 020 do Catalogo de Componentes — "Camera do aparelho".
//
// O ciclo de vida do `getUserMedia` num lugar so: pedir permissao, prender o stream no
// <video>, traduzir cada falha pra uma frase que o operador entende, e — a parte que mais
// se esquece — DESLIGAR a lente ao desmontar. Camera que fica ligada deixa a luzinha acesa
// e trava o aparelho pro proximo uso.
//
// Extraido do `CadastrarRostoModal` (cadastro de rosto do ponto), que era o unico codigo do
// sistema com esse ciclo tratado direito — o `PontoRegistrar` repetia uma versao mais
// pobre. Nasceu compartilhado pro reconhecimento de produto por camera (DA-392) nao criar a
// TERCEIRA copia.
//
// A diferenca entre os usos e uma prop: `facingMode` 'user' (a camera que ve VOCE: rosto,
// ponto) ou 'environment' (a camera que ve O QUE VOCE APONTA: produto na mao).

export type StatusCamera = 'parada' | 'pedindo' | 'pronta' | 'erro' | 'sem-suporte';

export interface OpcoesCamera {
  /** 'user' = camera frontal (rosto). 'environment' = traseira (a peca na mao). */
  facingMode?: 'user' | 'environment';
  largura?: number;
  altura?: number;
  /** Enquanto false a lente fica desligada. Serve pra so ligar quando o painel abre. */
  ativa?: boolean;
}

// Cada falha do getUserMedia tem um nome e uma SAIDA diferente. Mensagem generica
// ("erro ao abrir camera") deixa o operador sem saber se o problema e permissao, outra aba
// aberta, ou aparelho sem camera — tres consertos distintos.
function traduzirErro(err: unknown): string {
  const nome = (err as { name?: string })?.name || '';
  if (nome === 'NotAllowedError' || nome === 'PermissionDeniedError') {
    return 'Permissao da camera negada. Toque no cadeado da barra de endereco > Camera > Permitir, e atualize a pagina.';
  }
  if (nome === 'NotReadableError' || nome === 'TrackStartError') {
    return 'A camera esta em uso por outro aplicativo ou aba. Feche e tente de novo.';
  }
  if (nome === 'NotFoundError' || nome === 'DevicesNotFoundError') {
    return 'Nenhuma camera encontrada neste aparelho.';
  }
  if (nome === 'OverconstrainedError') {
    return 'A camera deste aparelho nao aceita a resolucao pedida.';
  }
  const msg = (err as { message?: string })?.message || String(err);
  return `Erro de camera (${nome || 'desconhecido'}): ${msg}`;
}

export function useCameraAparelho(opcoes: OpcoesCamera = {}) {
  const { facingMode = 'environment', largura = 1280, altura = 960, ativa = true } = opcoes;

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [status, setStatus] = useState<StatusCamera>('parada');
  const [erro, setErro] = useState('');
  // Muda a cada "Tentar de novo" — e o que faz o efeito re-rodar sem depender de remontar
  // o componente inteiro.
  const [tentativa, setTentativa] = useState(0);

  const desligar = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  useEffect(() => {
    if (!ativa) {
      desligar();
      setStatus('parada');
      return;
    }
    let cancelado = false;

    (async () => {
      setStatus('pedindo');
      setErro('');
      if (!navigator.mediaDevices?.getUserMedia) {
        setStatus('sem-suporte');
        setErro('Este navegador nao tem acesso a camera.');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          // `ideal` e nao valor fixo: com valor fixo, aparelho que nao tem exatamente essa
          // resolucao estoura OverconstrainedError em vez de entregar a mais proxima.
          video: { width: { ideal: largura }, height: { ideal: altura }, facingMode },
          audio: false,
        });
        // O componente pode ter desmontado enquanto o usuario decidia a permissao. Sem
        // isto, o stream fica ligado pra sempre — lente acesa, ninguem olhando.
        if (cancelado) { stream.getTracks().forEach((t) => t.stop()); return; }

        const track = stream.getVideoTracks()[0];
        if (!track || track.readyState !== 'live') {
          stream.getTracks().forEach((t) => t.stop());
          setStatus('erro');
          setErro('A camera respondeu, mas nao entregou imagem. Feche outros aplicativos que a usem e tente de novo.');
          return;
        }
        // Cabo/permissao caindo no meio do uso: sem isto a tela congela no ultimo quadro e
        // parece que a foto travou.
        track.addEventListener('ended', () => {
          setStatus('erro');
          setErro('A camera foi desconectada. Toque em "Tentar de novo".');
        });

        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          try { await videoRef.current.play(); } catch { /* autoplay barrado nao impede o quadro */ }
        }
        if (!cancelado) setStatus('pronta');
      } catch (err) {
        if (cancelado) return;
        setStatus('erro');
        setErro(traduzirErro(err));
      }
    })();

    return () => { cancelado = true; desligar(); };
  }, [ativa, facingMode, largura, altura, tentativa, desligar]);

  const tentarDeNovo = useCallback(() => setTentativa((n) => n + 1), []);

  /**
   * Congela o quadro atual num JPEG. Devolve `null` se a camera ainda nao entregou imagem
   * (o video comeca com 0x0 por alguns milissegundos — fotografar antes disso gera um
   * arquivo preto que o reconhecimento aceitaria calado).
   */
  const tirarFoto = useCallback(async (qualidade = 0.9): Promise<Blob | null> => {
    const v = videoRef.current;
    if (!v || !v.videoWidth || !v.videoHeight) return null;
    const canvas = document.createElement('canvas');
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
    return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/jpeg', qualidade));
  }, []);

  return { videoRef, status, erro, tirarFoto, tentarDeNovo, desligar };
}

/**
 * O aparelho TEM camera? Responde sem pedir permissao (o `enumerateDevices` lista os
 * dispositivos mesmo sem autorizacao — so nao revela o rotulo deles).
 *
 * Existe pra tela nao desenhar um botao de camera no PC de balcao sem webcam: recurso que
 * so avisa "indisponivel" DEPOIS do clique gasta a confianca do operador.
 */
export function useTemCamera(): boolean | null {
  const [tem, setTem] = useState<boolean | null>(null);
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        if (!navigator.mediaDevices?.enumerateDevices) { if (vivo) setTem(false); return; }
        const lista = await navigator.mediaDevices.enumerateDevices();
        if (vivo) setTem(lista.some((d) => d.kind === 'videoinput'));
      } catch {
        if (vivo) setTem(false);
      }
    })();
    return () => { vivo = false; };
  }, []);
  return tem;
}
